import { useState, useMemo } from "react";
import SEO from "../components/SEO";
import { Calculator, Users, DollarSign, Clock, TrendingUp, Home, ArrowRight, Menu, ChevronDown, Info } from "lucide-react";
import { Button } from "../components/ui/button";
import { Link, useNavigate } from "react-router-dom";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_5ead2d97-7ad4-48d6-8ee4-b13556106f69/artifacts/jd65gb5i_CityWorth%20Logo.png";

const HOMEOWNERSHIP_RATE = 0.35;
const RENTER_RATE = 0.65;
const RETENTION_MULTIPLIER = 3;

const formatCurrency = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
const formatNumber = (n) => new Intl.NumberFormat("en-US").format(n);

const SliderInput = ({ label, icon: Icon, value, setValue, min, max, step, format, suffix, prefix }) => (
  <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
          <Icon className="w-5 h-5 text-red-600" />
        </div>
        <span className="text-sm font-semibold text-slate-700">{label}</span>
      </div>
      <span className="text-2xl font-black text-slate-900" data-testid={`value-${label.toLowerCase().replace(/\s/g, '-')}`}>
        {prefix}{format ? format(value) : value}{suffix}
      </span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => setValue(Number(e.target.value))}
      className="w-full h-2 bg-slate-100 rounded-full appearance-none cursor-pointer accent-red-600"
      data-testid={`slider-${label.toLowerCase().replace(/\s/g, '-')}`}
    />
    <div className="flex justify-between text-xs text-slate-400 mt-2">
      <span>{prefix}{format ? format(min) : min}{suffix}</span>
      <span>{prefix}{format ? format(max) : max}{suffix}</span>
    </div>
  </div>
);

const RetentionCalculatorPage = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [employees, setEmployees] = useState(200);
  const [salary, setSalary] = useState(60000);
  const [tenure, setTenure] = useState(3.9);
  const [replacementPct, setReplacementPct] = useState(50);
  const [homeownershipIncrease, setHomeownershipIncrease] = useState(5);

  const results = useMemo(() => {
    const currentHomeowners = Math.round(employees * HOMEOWNERSHIP_RATE);
    const currentRenters = employees - currentHomeowners;
    const shifted = Math.round(employees * (homeownershipIncrease / 100));
    const replacementCost = salary * (replacementPct / 100);
    const avoidedReplacements = shifted * (RETENTION_MULTIPLIER - 1);
    const totalSavings = avoidedReplacements * replacementCost;

    const renterTenure = tenure / (HOMEOWNERSHIP_RATE * RETENTION_MULTIPLIER + RENTER_RATE);
    const homeownerTenure = renterTenure * RETENTION_MULTIPLIER;

    return {
      currentHomeowners,
      currentRenters,
      shifted,
      replacementCost,
      avoidedReplacements,
      totalSavings,
      renterTenure: renterTenure.toFixed(1),
      homeownerTenure: homeownerTenure.toFixed(1),
    };
  }, [employees, salary, tenure, replacementPct, homeownershipIncrease]);

  const menuItems = [
    { label: "Home", action: () => navigate("/") },
    { label: "How It Works", action: () => navigate("/how-it-works") },
    { label: "Retention Programs", action: () => navigate("/retention-programs") },
    { label: "Retention Analysis", action: () => navigate("/retention-analysis") },
    { label: "Employee Experience", action: () => navigate("/employee-experience") },
    { label: "Our Story", action: () => navigate("/our-story") },
    { label: "Retention Calculator", action: () => navigate("/retention-calculator") },
    { label: "Events", action: () => navigate("/events") },
    { label: "Book a Call", action: () => navigate("/book-a-call") },
    { label: "Careers", action: () => navigate("/careers") },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="retention-calculator-page">
      <SEO title="Retention Calculator" description="Calculate how much your company could save by increasing homeownership among employees. Homeowners stay 3x longer than renters." />
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-100 py-2">
        <div className="container-main flex items-center justify-between py-8 md:py-10">
          <Link to="/"><img src={LOGO_URL} alt="CityWorth" className="h-10 md:h-12" /></Link>
          <div className="flex items-center gap-4">
            <Link to="/get-started"><Button className="btn-primary text-white px-6 py-2.5">Get Started</Button></Link>
            <div className="relative">
              <Button variant="outline" onClick={() => setIsMenuOpen(!isMenuOpen)} className="flex items-center gap-2 border-slate-300 px-4 py-2.5">
                <Menu className="w-5 h-5" /><span className="hidden sm:inline">Menu</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${isMenuOpen ? 'rotate-180' : ''}`} />
              </Button>
              {isMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setIsMenuOpen(false)} />
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50">
                    {menuItems.map((item, i) => (
                      <button key={i} onClick={() => { item.action(); setIsMenuOpen(false); }} className="w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition-colors">{item.label}</button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-16 bg-white border-b border-slate-200">
        <div className="container-main text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-50 border border-red-100 mb-6">
            <Calculator className="w-4 h-4 text-red-600" />
            <span className="text-sm font-semibold text-red-700">Retention Calculator</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-black text-slate-900 mb-4">
            How Much Could You <span className="gradient-text">Save?</span>
          </h1>
          <p className="text-lg text-slate-600">
            See how increasing homeownership in your organization reduces turnover and saves money. Adjust the inputs below to model your company.
          </p>
        </div>
      </section>

      {/* Calculator */}
      <section className="py-16">
        <div className="container-main">
          <div className="grid lg:grid-cols-5 gap-8">

            {/* Inputs — Left */}
            <div className="lg:col-span-3 space-y-4">
              <h2 className="text-lg font-bold text-slate-900 mb-2">Your Organization</h2>

              <SliderInput label="Number of Employees" icon={Users} value={employees} setValue={setEmployees} min={50} max={5000} step={10} format={formatNumber} prefix="" suffix="" />
              <SliderInput label="Median Salary" icon={DollarSign} value={salary} setValue={setSalary} min={30000} max={200000} step={5000} prefix="$" suffix="" format={formatNumber} />
              <SliderInput label="Median Tenure" icon={Clock} value={tenure} setValue={setTenure} min={1} max={10} step={0.1} prefix="" suffix=" yrs" />
              <SliderInput label="Cost to Replace" icon={TrendingUp} value={replacementPct} setValue={setReplacementPct} min={20} max={200} step={5} prefix="" suffix="%" />
              <SliderInput label="Homeownership Increase" icon={Home} value={homeownershipIncrease} setValue={setHomeownershipIncrease} min={1} max={30} step={1} prefix="" suffix="%" />

              {/* Fixed data */}
              <div className="bg-slate-100 rounded-2xl p-5 border border-slate-200 flex items-start gap-3">
                <Info className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-slate-500">
                  <span className="font-semibold text-slate-700">Based on real workforce data:</span> Current homeownership rate is <span className="font-bold text-slate-700">35%</span>, renter rate is <span className="font-bold text-slate-700">65%</span>, and homeowners stay <span className="font-bold text-slate-700">3x longer</span> than renters across all different types of industries.
                </div>
              </div>
            </div>

            {/* Results — Right */}
            <div className="lg:col-span-2 space-y-4">
              <h2 className="text-lg font-bold text-slate-900 mb-2">Projected Savings</h2>

              {/* Big number */}
              <div className="bg-gradient-to-br from-green-600 via-green-700 to-green-800 rounded-2xl p-8 text-center shadow-lg" data-testid="total-savings">
                <p className="text-sm font-semibold text-white/70 uppercase tracking-wider mb-2">Total Avoided Replacement Cost</p>
                <p className="text-4xl md:text-5xl font-black text-white">{formatCurrency(results.totalSavings)}</p>
              </div>

              {/* Breakdown cards */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm text-center">
                  <p className="text-3xl font-black text-slate-900" data-testid="shifted-employees">{results.shifted}</p>
                  <p className="text-sm text-slate-500 mt-1">Employees Shifted to Homeowner</p>
                </div>
                <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm text-center">
                  <p className="text-3xl font-black text-slate-900">{results.avoidedReplacements}</p>
                  <p className="text-sm text-slate-500 mt-1">Avoided Replacements</p>
                </div>
                <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm text-center">
                  <p className="text-3xl font-black text-slate-900">{formatCurrency(results.replacementCost)}</p>
                  <p className="text-sm text-slate-500 mt-1">Cost per Replacement</p>
                </div>
                <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm text-center">
                  <p className="text-3xl font-black text-red-600">3x</p>
                  <p className="text-sm text-slate-500 mt-1">Longer Retention for Homeowners</p>
                </div>
              </div>

              {/* Tenure breakdown */}
              <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
                <p className="text-sm font-semibold text-slate-700 mb-4">Estimated Tenure Breakdown</p>
                <div className="space-y-3">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-slate-600">Homeowner Tenure</span>
                      <span className="text-sm font-bold text-slate-900">{results.homeownerTenure} years</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-3">
                      <div className="bg-red-500 h-3 rounded-full transition-all duration-500" style={{ width: `${Math.min((results.homeownerTenure / 15) * 100, 100)}%` }} />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-slate-600">Renter Tenure</span>
                      <span className="text-sm font-bold text-slate-900">{results.renterTenure} years</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-3">
                      <div className="bg-slate-300 h-3 rounded-full transition-all duration-500" style={{ width: `${Math.min((results.renterTenure / 15) * 100, 100)}%` }} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Workforce snapshot */}
              <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
                <p className="text-sm font-semibold text-slate-700 mb-4">Current Workforce Snapshot</p>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-slate-600">Homeowners (35%)</span>
                      <span className="text-sm font-bold text-slate-900">{results.currentHomeowners}</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-3">
                      <div className="bg-green-500 h-3 rounded-full" style={{ width: '35%' }} />
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4 mt-3">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-slate-600">Renters (65%)</span>
                      <span className="text-sm font-bold text-slate-900">{results.currentRenters}</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-3">
                      <div className="bg-slate-400 h-3 rounded-full" style={{ width: '65%' }} />
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <Link to="/get-started" className="block">
                <Button size="lg" className="btn-primary text-white w-full py-6 text-lg font-bold shadow-lg hover:shadow-xl transition-all">
                  Book a Retention Strategy Session <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-white border-t border-slate-200">
        <div className="container-main max-w-4xl">
          <h2 className="text-2xl font-black text-slate-900 text-center mb-10">How the Math Works</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: "1", title: "Employees Shift", desc: "A percentage of your workforce moves from renting to homeownership through CityWorth's benefit." },
              { step: "2", title: "Tenure Increases", desc: "Homeowners stay 3x longer than renters based on real workforce data across all different types of industries." },
              { step: "3", title: "Turnover Drops", desc: "Each shifted employee avoids 2 replacement cycles compared to remaining a renter." },
              { step: "4", title: "You Save", desc: "Avoided replacements multiplied by your cost to replace equals your total savings." },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-10 h-10 rounded-full bg-red-600 text-white font-bold flex items-center justify-center mx-auto mb-3">{item.step}</div>
                <h3 className="font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-white border-t border-slate-200">
        <div className="container-main flex flex-col md:flex-row items-center justify-between gap-6">
          <Link to="/"><img src={LOGO_URL} alt="CityWorth" className="h-8" /></Link>
          <div className="flex flex-wrap items-center justify-center gap-6">
            <Link to="/" className="text-slate-600 hover:text-slate-900">Home</Link>
            <Link to="/how-it-works" className="text-slate-600 hover:text-slate-900">How It Works</Link>
            <Link to="/retention-programs" className="text-slate-600 hover:text-slate-900">Programs</Link>
            <Link to="/our-story" className="text-slate-600 hover:text-slate-900">Our Story</Link>
            <Link to="/retention-calculator" className="text-slate-600 hover:text-slate-900">Calculator</Link>
            <Link to="/events" className="text-slate-600 hover:text-slate-900">Events</Link>
          </div>
          <p className="text-slate-500 text-sm">&copy; {new Date().getFullYear()} CityWorth</p>
        </div>
      </footer>
    </div>
  );
};

export default RetentionCalculatorPage;
